import { create } from '@bufbuild/protobuf'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import {
    GrpcClientsContextValue,
    useGrpcClients,
} from '@/contexts/GrpcClientContext'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import {
    TrackingFiltersDto,
    TrackingFiltersDtoSchema,
} from '@/generated/Messages/Filters_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { loadAllIter } from '@/helper/loadAllIter'
import {
    listAllQueryKeyPrefix,
    listQueryKeyPrefix,
    trackingsQueryKeyPrefix,
} from '@/helper/queryKeyPrefixes'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

export const trackingsListQueryKeyPrefix = [trackingsQueryKeyPrefix] as const

export const trackingsQueryOptions = (
    clients: Partial<GrpcClientsContextValue> | null,
    filter: Partial<OmitDeepMessageFields<TrackingFiltersDto>> = {},
    loadAll = false
) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryOptions({
        queryKey:
            loadAll ?
                [...trackingsListQueryKeyPrefix, listAllQueryKeyPrefix, filter]
            :   [...trackingsListQueryKeyPrefix, listQueryKeyPrefix, filter],
        queryFn: async ({ signal }) => {
            assertNotNullish(clients?.tracking)

            const internalFilter = create(TrackingFiltersDtoSchema, {
                sorting: filter.sorting ?? [],
                deletedItemSelection:
                    filter.deletedItemSelection ??
                    DeletedItemSelection.NORMAL_ONLY,
                ids: filter.ids ?? [],
                searching: filter.searching ?? [],
                searchOperator: filter.searchOperator,
                pagination: filter.pagination,
                userIds: filter.userIds ?? [],
                dateTimeFrom: filter.dateTimeFrom,
                dateTimeTo: filter.dateTimeTo,
                hasEnded: filter.hasEnded,
                projectIds: filter.projectIds ?? [],
                mostRecentlyModifiedByService:
                    filter.mostRecentlyModifiedByService,
            })
            if (!loadAll) {
                const response = await clients.tracking.getTrackings(
                    internalFilter,
                    { signal }
                )

                return response
            }

            const trackings = (
                await Array.fromAsync(
                    loadAllIter(
                        async f => {
                            const { trackings: data, pagination } =
                                (await clients.tracking?.getTrackings(f, {
                                    signal,
                                })) ?? {}

                            return { data, pagination }
                        },
                        internalFilter,
                        1000
                    )
                )
            ).flat()

            return {
                pagination: { totalItemCount: trackings.length },
                trackings,
            }
        },
        refetchInterval: 1000 * 60,
    })

export const useTrackings = (
    filter: Partial<OmitDeepMessageFields<TrackingFiltersDto>> = {},
    loadAll = false
) => {
    const clients = useGrpcClients()

    return useSuspenseQuery(trackingsQueryOptions(clients, filter, loadAll))
}
